import { EventEmitter } from '../util/eventemitter';
import { StateManager } from '../states/statemanager';

export class SkillData extends EventEmitter {
  skills: any[];
  private cooldowns: Map<number, { startedAt: number; durationMs: number }>; // skillIdx -> cooldown

  constructor() {
    super();
    this.skills = [];
    this.cooldowns = new Map();
  }

  useSkill(skill: any) {
    // LIST_SKILL.STB rows 17 (Drive Cart) and 27 (Drive Castle Gear) both
    // use basic command 7, so the command alone cannot select the vehicle.
    // The legacy skill list may also supply synthetic IDs 5000/5001.
    if ([17, 27, 5000, 5001].includes(skill.skillIdx)) {
      netGame.toggleDrive(skill.skillIdx === 27 || skill.skillIdx === 5001);
      return;
    }

    const skillData = GDM.getNow('skill_data');
    const data = skillData.getData(skill.skillIdx);
    const type = parseInt(data[SKILL.TYPE]);

    switch(type) {
    case SKILL_ACTION_TYPE.BASE_ACTION:
      const command = parseInt(data[SKILL.BASIC_COMMAND]);
      this._useCommand(command);
      break;
    case SKILL_ACTION_TYPE.EMOTION_ACTION:
      const motion = parseInt(data[SKILL.ANI_ACTION_TYPE]);
      netGame.setMotion(motion, 1);
      break;
    case SKILL_ACTION_TYPE.ACTION_TARGET_BOUND:
    case SKILL_ACTION_TYPE.ACTION_TARGET_BOUND_DURATION:
    case SKILL_ACTION_TYPE.ACTION_TARGET_STATE_DURATION:
    case SKILL_ACTION_TYPE.ACTION_SELF_AND_TARGET:
    case SKILL_ACTION_TYPE.ACTION_RESURRECTION:
    case SKILL_ACTION_TYPE.ACTION_IMMEDIATE:
    case SKILL_ACTION_TYPE.ACTION_ENFORCE_WEAPON:
    case SKILL_ACTION_TYPE.ACTION_ENFORCE_BULLET:
    case SKILL_ACTION_TYPE.ACTION_FIRE_BULLET:
      if (MC.target && MC.target.object) {
        const targetObj = MC.target.object;
        GC.useSkillOnTarget(targetObj, skill.slot, data);
      }
      break;
    case SKILL_ACTION_TYPE.ACTION_SELF_BOUND:
    case SKILL_ACTION_TYPE.ACTION_SELF_DAMAGE:
    case SKILL_ACTION_TYPE.ACTION_SELF_BOUND_DURATION:
    case SKILL_ACTION_TYPE.ACTION_SELF_STATE_DURATION:
    case SKILL_ACTION_TYPE.ACTION_SUMMON_PET:
      netGame.useSkillOnSelf(skill.slot);
      break;
    case SKILL_ACTION_TYPE.ACTION_PASSIVE:
      GCM.system('This is a passive skill.');
      break;
    case SKILL_ACTION_TYPE.ACTION_AREA_TARGET:
    case SKILL_ACTION_TYPE.ACTION_WARP:
      // Needs a ground-position skill-cast packet: only useSkillOnSelf/useSkillOnTarget
      // exist today. See docs/superpowers/specs/2026-09-07-skill-core-completion-design.md.
      console.warn('TODO: Unimplemented useSkill (needs a position-target packet)', skill);
      break;
    case SKILL_ACTION_TYPE.CREATE_WINDOW:
      // Needs a shop/workshop dialog that doesn't exist yet (see TODO.md #25).
      console.warn('TODO: Unimplemented useSkill (needs shop/workshop UI)', skill);
      break;
    default:
      console.warn('TODO: Unimplemented useSkill', skill);
      break;
    }
  }

  _useCommand(command: any) {
    const target = MC.target;
    switch (command) {
    case BASIC_COMMAND.PARTY:
      if (target) {
        if (MC.party.exists) {
          if (MC.party.leaderTag === MC.uniqueTag) {
            netGame.partyRequest(PARTY_REQ_JOIN, target.serverObjectIdx);
          } else {
            GCM.system('Only the party leader can send party invites.');
          }
        } else {
          netGame.partyRequest(PARTY_REQ_MAKE, target.serverObjectIdx);
        }
      }
      break;
    case BASIC_COMMAND.SIT:
      netGame.toggleSit();
      break;
    case BASIC_COMMAND.PICK_ITEM: {
      const item = GZM.objects && GZM.objects.findNearestItem(MC.position);
      if (item) {
        GC.pickupItem(item);
      } else {
        GCM.system('No item found nearby.');
      }
      break;
    }
    case BASIC_COMMAND.JUMP:
    case BASIC_COMMAND.AIR_JUMP:
      MC.jump();
      break;
    case BASIC_COMMAND.AUTO_TARGET: {
      const mob = GZM.objects && GZM.objects.findNearestMob(MC.position);
      if (mob) {
        StateManager.get('game').setTarget(mob);
      } else {
        GCM.system('No target found.');
      }
      break;
    }
    case BASIC_COMMAND.SELF_TARGET:
      StateManager.get('game').setTarget(MC);
      break;
    case BASIC_COMMAND.ATTACK:
      if (target && target.object) {
        GC.attackObj(target.object);
      } else {
        GCM.system('No target selected.');
      }
      break;
    case BASIC_COMMAND.DRIVE_CART:
    case BASIC_COMMAND.CASTLE_GEAR:
      netGame.toggleDrive(command === BASIC_COMMAND.CASTLE_GEAR);
      break;
    case BASIC_COMMAND.BOARD_CART:
      // Passenger seating (riding a cart someone else is driving) is a
      // separate, out-of-scope multiplayer feature -- see spec.
      console.warn('TODO: Unimplemented basic command (cart passenger seat)', command);
      break;
    case BASIC_COMMAND.ADD_FRIEND:
      // Friends list doesn't exist yet.
      console.warn('TODO: Unimplemented basic command (friends list)', command);
      break;
    case BASIC_COMMAND.EXCHANGE:
      if (target && target.object) {
        netGame.tradeRequest(target.object.serverObjectIdx);
      } else {
        GCM.system('No target selected.');
      }
      break;
    case BASIC_COMMAND.PRIVATE_STORE:
      MC.vending.startSetup();
      break;
    default:
      console.warn('Used unimplemented basic command:', command);
      break;
    }
  }

  setSkills(skills: any) {
    this.skills = skills;
    this.emit('changed');
  }

  appendSkills(skills: any) {
    this.skills = this.skills.concat(skills);
    this.emit('changed');
  }

  findBySlot(slotNo: any) {
    for (let i = 0; i < this.skills.length; ++i) {
      if (this.skills[i].slot === slotNo) {
        return this.skills[i];
      }
    }

    return null;
  }

  setCooldown(skillIdx: number, cooldownMs: number) {
    this.cooldowns.set(skillIdx, { startedAt: Date.now(), durationMs: cooldownMs });
    this.emit('changed');
  }

  getCooldown(skillIdx: number): { startedAt: number; durationMs: number } | null {
    const entry = this.cooldowns.get(skillIdx);
    if (!entry) {
      return null;
    }
    if (entry.startedAt + entry.durationMs <= Date.now()) {
      return null;
    }
    return entry;
  }
}
